import { uiText } from "@/lib/i18n/uiText";
import { Link } from "@/lib/router-compat";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { InternalLinks } from "@/components/seo/InternalLinks";
import { LegalDisclaimer } from "@/components/shared/LegalDisclaimer";
import { useSEO } from "@/lib/useSEO";
import { breadcrumbJsonLd, organizationJsonLd, webPageJsonLd } from "@/lib/seoSchema";
import { SITE_URL } from "@/lib/entities";
import { NEWS_POSTS } from "@/lib/blog";

const CANONICAL = "/blog/news";

const crumbs = [
  { name: "Home", path: "/" },
  { name: "Blog", path: "/blog" },
  { name: "News", path: CANONICAL },
];

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

/**
 * Newsroom hub at `/blog/news`.
 *
 * Lists every editorial item flagged `category: "news"` in the blog registry and
 * emits `NewsArticle` JSON-LD so the items qualify for Google News / Discover
 * surfaces alongside the existing news sitemap.
 */
export default function NewsIndex() {
  const posts = NEWS_POSTS;
  const latest = posts[0]?.date;

  useSEO({
    title: "Statlign News — Sports Intelligence Newsroom",
    description:
      "Model updates, market moves and coverage expansion from the Statlign sports intelligence newsroom. Dated, sourced and updated by our editorial team.",
    canonicalPath: CANONICAL,
    keywords:
      "statlign news, sports intelligence news, football prediction news, betting model updates, sports betting market news",
    jsonLd: [
      organizationJsonLd(),
      webPageJsonLd({
        name: "Statlign News — Sports Intelligence Newsroom",
        description:
          "Model updates, market moves and coverage expansion from the Statlign editorial team.",
        path: CANONICAL,
      }),
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "Statlign Newsroom",
        url: `${SITE_URL}${CANONICAL}`,
        isPartOf: { "@id": `${SITE_URL}/#website` },
        publisher: { "@id": `${SITE_URL}/#organization` },
        mainEntity: {
          "@type": "ItemList",
          itemListElement: posts.map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `${SITE_URL}/blog/${p.slug}`,
            name: p.title,
          })),
        },
      },
      ...posts.map((p) => ({
        "@context": "https://schema.org",
        "@type": "NewsArticle",
        headline: p.title,
        description: p.description,
        url: `${SITE_URL}/blog/${p.slug}`,
        mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/blog/${p.slug}` },
        datePublished: p.date,
        dateModified: p.date,
        author: { "@type": "Organization", name: "Statlign", url: SITE_URL },
        publisher: { "@id": `${SITE_URL}/#organization` },
      })),
      breadcrumbJsonLd(crumbs),
    ],
  });

  return (
    <div className="mx-auto max-w-4xl space-y-8 animate-fade-in">
      <Breadcrumbs items={crumbs} className="text-sm text-muted-foreground" />

      <header className="space-y-2">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
           {uiText("Statlign News — Sports Intelligence Newsroom")} </h1>
        <p className="max-w-3xl text-muted-foreground">
           {uiText("Model updates, market intelligence and coverage expansion, written and dated by the Statlign editorial team. Every item links back to the fixtures and data behind it.")} </p>
        {latest && (
          <p className="text-xs text-muted-foreground">{uiText("Last updated")} {formatDate(latest)}</p>
        )}
      </header>

      {posts.length === 0 ? (
        <p className="text-muted-foreground">
           {uiText("No newsroom items yet. In the meantime, our long-form analysis lives on the")}{" "}
          <Link to="/blog" className="text-primary hover:underline">
             {uiText("Statlign blog")} </Link>
          .
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {posts.map((post) => (
            <Link key={post.slug} to={`/blog/${post.slug}`} className="group">
              <Card className="h-full transition-colors hover:border-primary/50">
                <CardHeader>
                  <div className="mb-2 flex flex-wrap gap-1.5">
                    {post.tags.map((t) => (
                      <Badge key={t} variant="secondary" className="text-xs">
                        {t}
                      </Badge>
                    ))}
                  </div>
                  <CardTitle className="text-lg group-hover:text-primary">{uiText(post.title)}</CardTitle>
                  <CardDescription>{uiText(post.description)}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-muted-foreground">
                    {formatDate(post.date)} · {post.author}
                  </p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">{uiText("How the Statlign newsroom works")}</h2>
        <p className="text-sm text-muted-foreground">
           {uiText("Newsroom items are editorial, not predictions. When a story references a selection, the underlying fixture page carries the full evidence: strategy triggers, confidence score, odds provenance and the data sources used. We label estimates as estimates, and we never describe any selection as guaranteed — betting always carries risk.")} </p>
      </section>

      <InternalLinks />

      <LegalDisclaimer />
    </div>
  );
}
